import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Trash2, Eye, Mail, Phone, X, CheckCircle, Loader2, Search, Inbox } from "lucide-react";
import api from "../../lib/api";
import Pagination from "../../components/admin/Pagination";

const BADGES = {
  new: "admin-badge-new",
  read: "admin-badge-read",
  responded: "admin-badge-responded",
};

const AdminInquiries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/inquiries");
      setItems(res.data);
    } catch (e) { toast.error("Failed to load inquiries"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setItems((p) => p.map((i) => (i.id === id ? { ...i, status } : i)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (filter !== "all") list = list.filter((i) => i.status === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((i) =>
        [i.name, i.email, i.phone, i.service, i.message].some((v) => (v || "").toLowerCase().includes(q)),
      );
    }
    return list;
  }, [items, filter, query]);

  useEffect(() => { setPage(1); }, [filter, query, pageSize]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const counts = {
    all: items.length,
    new: items.filter((i) => i.status === "new").length,
    read: items.filter((i) => i.status === "read").length,
    responded: items.filter((i) => i.status === "responded").length,
  };

  return (
    <div data-testid="admin-inquiries-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Inquiries</h1>
          <p className="mt-1 text-sm text-slate-500">Manage and respond to messages from your contact form.</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, message..." className="admin-input pl-9" data-testid="inq-search" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 mb-6 bg-slate-100 p-1 rounded-lg w-full md:w-fit overflow-x-auto admin-scroll">
        {[
          { k: "all", l: "All" },
          { k: "new", l: "New" },
          { k: "read", l: "Read" },
          { k: "responded", l: "Responded" },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setFilter(s.k)}
            className={`px-3.5 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${
              filter === s.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid={`inq-filter-${s.k}`}
          >
            {s.l} <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${filter === s.k ? "bg-orange-100 text-orange-700" : "bg-slate-200 text-slate-600"}`}>{counts[s.k]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {[...Array(5)].map((_, i) => <div key={i} className="p-4"><div className="skeleton h-10 rounded-lg" /></div>)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 text-slate-400 rounded-full mb-4">
            <Inbox size={22} />
          </div>
          <h3 className="font-semibold text-slate-900">No inquiries found</h3>
          <p className="mt-1 text-sm text-slate-500">{query ? "Try a different search term." : "When visitors fill out your contact form, you'll see them here."}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto admin-scroll">
            <table className="w-full hidden md:table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Contact</th>
                  <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Service</th>
                  <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Date</th>
                  <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paged.map((i) => (
                  <tr key={i.id} className="hover:bg-slate-50 transition-colors" data-testid={`inq-row-${i.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900 text-sm">{i.name}</div>
                      <div className="text-xs text-slate-500">{i.email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">{i.service}</td>
                    <td className="px-6 py-4 text-xs text-slate-500 whitespace-nowrap">{new Date(i.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4"><span className={`admin-badge ${BADGES[i.status]}`}>{i.status}</span></td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => { setSelected(i); if (i.status === "new") updateStatus(i.id, "read"); }} className="admin-btn-ghost p-2" title="View" data-testid={`inq-view-${i.id}`}><Eye size={14} /></button>
                        <button onClick={() => remove(i.id)} className="admin-btn-ghost p-2 hover:text-red-600" title="Delete" data-testid={`inq-delete-${i.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-100">
            {filtered.map((i) => (
              <div key={i.id} className="p-4" data-testid={`inq-card-${i.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{i.name}</div>
                    <div className="text-xs text-slate-500 truncate">{i.email}</div>
                    <div className="text-xs text-slate-700 mt-1">{i.service}</div>
                  </div>
                  <span className={`admin-badge shrink-0 ${BADGES[i.status]}`}>{i.status}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => { setSelected(i); if (i.status === "new") updateStatus(i.id, "read"); }} className="admin-btn-secondary flex-1">View</button>
                  <button onClick={() => remove(i.id)} className="admin-btn-danger"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="px-4">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={filtered.length}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 20, 30, 50]}
            />
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 admin-modal-backdrop flex items-center justify-center p-3 md:p-6" onClick={() => setSelected(null)} data-testid="inq-detail-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl admin-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3.5 border-b border-slate-200 flex justify-between items-start gap-4 shrink-0">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600">Inquiry</p>
                <h3 className="text-lg font-bold text-slate-900 truncate">{selected.name}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="admin-btn-ghost p-2" data-testid="inq-modal-close"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto admin-scroll flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 hover:bg-slate-50">
                  <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center"><Mail size={16} /></div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Email</div>
                    <div className="text-sm font-medium text-slate-900 break-all">{selected.email}</div>
                  </div>
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 hover:bg-slate-50">
                    <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center"><Phone size={16} /></div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Phone</div>
                      <div className="text-sm font-medium text-slate-900">{selected.phone}</div>
                    </div>
                  </a>
                )}
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Service</div>
                <div className="font-semibold text-slate-900 mt-0.5">{selected.service}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Message</div>
                <p className="whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-800 leading-relaxed">{selected.message}</p>
              </div>
              <div className="text-xs text-slate-500">Received {new Date(selected.created_at).toLocaleString()}</div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex flex-col sm:flex-row gap-2 shrink-0">
              <button onClick={() => updateStatus(selected.id, "read")} disabled={selected.status === "read"} className="admin-btn-secondary flex-1" data-testid="inq-mark-read">Mark Read</button>
              <button onClick={() => updateStatus(selected.id, "responded")} disabled={selected.status === "responded"} className="admin-btn-primary flex-1" data-testid="inq-mark-responded"><CheckCircle size={14} /> Responded</button>
              <button onClick={() => remove(selected.id)} className="admin-btn-danger" data-testid="inq-modal-delete"><Trash2 size={14} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInquiries;
